import { ClientError } from 'graphql-request';
import { ErrorCode, ErrorCodeError, hasErrorCode } from '../graphql';
import { HandleInviteFirmClientErrorCallbacks } from './types';

// hasErrorCode throws on plain non-GraphQL errors (it falls through to
// reading `response.errors` off the value), so guard the lookup here.
const safeHasErrorCode = (
  error: Error | unknown,
  code: ErrorCode
): boolean => {
  try {
    return hasErrorCode(error, code);
  } catch {
    return false;
  }
};

// A deterministic backend refusal: the server answered the request and
// returned GraphQL errors (graphql-request throws a ClientError carrying
// them). Retrying these can never succeed. Transient failures (network
// blips, timeouts) don't have this shape and remain retryable.
const isDeterministicGraphqlRefusal = (error: Error | unknown): boolean => {
  if (error instanceof ErrorCodeError) return true;
  const errors = (error as ClientError | undefined)?.response?.errors;
  return Array.isArray(errors) && errors.length > 0;
};

// React Query retry predicate for invite mutations: never retry a
// deterministic refusal, otherwise keep the previous 3-attempt behavior.
export const shouldRetryInviteFirmClient = (
  failureCount: number,
  error: Error | unknown
): boolean => !isDeterministicGraphqlRefusal(error) && failureCount < 3;

export const handleInviteFirmClientError = (
  error: Error | unknown,
  callbacks: HandleInviteFirmClientErrorCallbacks
) => {
  if (safeHasErrorCode(error, ErrorCode.UserAlreadyExists)) {
    callbacks.onUserAlreadyExists?.();
    return;
  }
  callbacks.onUnknownError?.();
};
